/**
 * Per-tab, memory-only runtime of the Storage Explorer: navigation history and
 * the request / cursor bookkeeping the main process must be told about when a
 * tab goes away. Listing pages live in TanStack Query; the tab content keeps
 * identity + view configuration only (see `StorageExplorerTabContent`).
 *
 * Disposal is idempotent and covers every path that drops a tab
 * (`removeTab`, `restoreTabs`, `closeOtherTabs`, `closeTabsToRight`,
 * `closeUnpinnedTabs`) through `releaseStorageExplorerTab`.
 */
import { create } from 'zustand';
import type { StorageResourceRef } from '../../../../types/azureStorage';
import { cancelStorageRequest, releaseStorageCursor } from '../services/azureStorageApi';
import { cancelStorageTabDownloads } from '../services/storageDownload';
import type { PendingStorageFolder } from '../mutations/pendingFolders';
import { reconcilePendingFolders as reconcileFolders } from '../mutations/pendingFolders';
import { releaseQueueMessages } from '../messages/queueMessagesStore';

export type StorageExplorerHistory = { back: StorageResourceRef[]; forward: StorageResourceRef[] };

type StorageExplorerRuntime = {
  connectionId: string;
  history: StorageExplorerHistory;
  /** requestIds of listings in flight for this tab (cancelled on disposal). */
  inFlight: string[];
  /** cursor handles the main process holds for this tab (released on disposal / navigation). */
  cursors: string[];
  pendingFolders: PendingStorageFolder[];
};

type StorageExplorerState = {
  byTab: Record<string, StorageExplorerRuntime>;
  ensure: (tabId: string, connectionId: string) => void;
  pushHistory: (tabId: string, from: StorageResourceRef) => void;
  popBack: (tabId: string, current: StorageResourceRef) => StorageResourceRef | undefined;
  popForward: (tabId: string, current: StorageResourceRef) => StorageResourceRef | undefined;
  trackRequest: (tabId: string, requestId: string) => void;
  untrackRequest: (tabId: string, requestId: string) => void;
  trackCursor: (tabId: string, cursorHandle: string) => void;
  addPendingFolder: (tabId: string, folder: PendingStorageFolder) => void;
  reconcilePendingFolders: (tabId: string, items: import('../../../../types/azureStorage').StorageItem[]) => void;
  /** Cancels in-flight listings and releases every cursor, keeping history (navigation / filter reset). */
  resetListing: (tabId: string) => void;
  /** Full disposal: `resetListing` + drop the entry. Safe to call twice. */
  release: (tabId: string) => void;
};

const EMPTY_HISTORY: StorageExplorerHistory = { back: [], forward: [] };

function runtimeFor(state: StorageExplorerState, tabId: string, connectionId: string): StorageExplorerRuntime {
  return state.byTab[tabId] ?? { connectionId, history: EMPTY_HISTORY, inFlight: [], cursors: [], pendingFolders: [] };
}

function releaseBackend(runtime: StorageExplorerRuntime | undefined): void {
  if (!runtime) return;
  for (const requestId of runtime.inFlight) void cancelStorageRequest(requestId);
  for (const handle of runtime.cursors) void releaseStorageCursor(runtime.connectionId, handle);
}

export const useStorageExplorerStore = create<StorageExplorerState>((set, get) => ({
  byTab: {},
  ensure: (tabId, connectionId) =>
    set((state) => (state.byTab[tabId] ? state : { byTab: { ...state.byTab, [tabId]: runtimeFor(state, tabId, connectionId) } })),
  pushHistory: (tabId, from) =>
    set((state) => {
      const runtime = state.byTab[tabId];
      if (!runtime) return state;
      return { byTab: { ...state.byTab, [tabId]: { ...runtime, history: { back: [...runtime.history.back, from], forward: [] } } } };
    }),
  popBack: (tabId, current) => {
    const runtime = get().byTab[tabId];
    if (!runtime || runtime.history.back.length === 0) return undefined;
    const back = runtime.history.back.slice(0, -1);
    const target = runtime.history.back[runtime.history.back.length - 1];
    set((state) => ({ byTab: { ...state.byTab, [tabId]: { ...runtime, history: { back, forward: [current, ...runtime.history.forward] } } } }));
    return target;
  },
  popForward: (tabId, current) => {
    const runtime = get().byTab[tabId];
    if (!runtime || runtime.history.forward.length === 0) return undefined;
    const [target, ...forward] = runtime.history.forward;
    set((state) => ({ byTab: { ...state.byTab, [tabId]: { ...runtime, history: { back: [...runtime.history.back, current], forward } } } }));
    return target;
  },
  trackRequest: (tabId, requestId) =>
    set((state) => {
      const runtime = state.byTab[tabId];
      if (!runtime) return state;
      return { byTab: { ...state.byTab, [tabId]: { ...runtime, inFlight: [...runtime.inFlight, requestId] } } };
    }),
  untrackRequest: (tabId, requestId) =>
    set((state) => {
      const runtime = state.byTab[tabId];
      if (!runtime) return state;
      return { byTab: { ...state.byTab, [tabId]: { ...runtime, inFlight: runtime.inFlight.filter((id) => id !== requestId) } } };
    }),
  trackCursor: (tabId, cursorHandle) =>
    set((state) => {
      const runtime = state.byTab[tabId];
      if (!runtime || runtime.cursors.includes(cursorHandle)) return state;
      return { byTab: { ...state.byTab, [tabId]: { ...runtime, cursors: [...runtime.cursors, cursorHandle] } } };
    }),
  addPendingFolder: (tabId, folder) =>
    set((state) => {
      const runtime = state.byTab[tabId];
      if (!runtime || runtime.pendingFolders.some((item) => item.container === folder.container && item.prefix === folder.prefix)) return state;
      return { byTab: { ...state.byTab, [tabId]: { ...runtime, pendingFolders: [...runtime.pendingFolders, folder] } } };
    }),
  reconcilePendingFolders: (tabId, items) =>
    set((state) => {
      const runtime = state.byTab[tabId];
      if (!runtime) return state;
      const pendingFolders = reconcileFolders(runtime.pendingFolders, items);
      return pendingFolders.length === runtime.pendingFolders.length
        ? state
        : { byTab: { ...state.byTab, [tabId]: { ...runtime, pendingFolders } } };
    }),
  resetListing: (tabId) => {
    const runtime = get().byTab[tabId];
    if (!runtime) return;
    releaseBackend(runtime);
    set((state) => ({ byTab: { ...state.byTab, [tabId]: { ...runtime, inFlight: [], cursors: [] } } }));
  },
  release: (tabId) => {
    const runtime = get().byTab[tabId];
    if (!runtime) return;
    releaseBackend(runtime);
    set((state) => {
      const next = { ...state.byTab };
      delete next[tabId];
      return { byTab: next };
    });
  },
}));

const IDLE_HISTORY: StorageExplorerHistory = EMPTY_HISTORY;

export function useStorageExplorerHistory(tabId: string): StorageExplorerHistory {
  return useStorageExplorerStore((s) => s.byTab[tabId]?.history ?? IDLE_HISTORY);
}

/** Called by `workspaceStore` on every tab-dropping path; idempotent. */
export function releaseStorageExplorerTab(tabId: string): void {
  useStorageExplorerStore.getState().release(tabId);
  releaseQueueMessages(tabId);
  cancelStorageTabDownloads(tabId); // integration review C1: an unanswered download (preflight / picker) stops
}
